/**
 * SharedChatBanner — shown when a user opens a Copilot thread they don't own
 * (e.g. via a Shared Space). Visually mirrors the global SyncBanner band so
 * users get a consistent "informational header" treatment, but uses an info
 * tone (vs. SyncBanner's warning tone) since this is informational, not an
 * in-flight job.
 *
 * Behaviour:
 * - Renders only when `isOwner === false` for the currently-loaded thread.
 * - Dismissible in-memory only: closing hides it for the rest of this view,
 *   but the banner re-appears on reload, on switching threads and back, and
 *   in any other tab — by design, since it conveys an important contextual
 *   fact (your messages are visible to the chat's owner) that we don't want
 *   the user to lose track of.
 */
import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import GroupRounded from '@mui/icons-material/GroupRounded';
import { useCopilotStore } from '../stores/useCopilotStore';

export default function SharedChatBanner() {
  const theme = useTheme();
  const threadId = useCopilotStore((s) => s.threadId);
  const ownership = useCopilotStore((s) => s.threadOwnership);

  const [dismissed, setDismissed] = React.useState<boolean>(false);

  // Reset dismissal whenever the loaded thread changes, so the banner
  // re-appears when navigating to another shared chat.
  React.useEffect(() => {
    setDismissed(false);
  }, [threadId]);

  if (!threadId) return null;
  if (ownership.isOwner !== false) return null;
  if (dismissed) return null;

  // Tone matches the global SyncBanner so the two banners read as the same
  // band of "important contextual chrome" sitting above the page content.
  const tokens = theme.tokens;
  const accent = tokens.semantic.warning;
  const accentSoft = tokens.semantic.warningSoft;
  const titleColor = tokens.text.primary;
  const bodyColor = tokens.text.secondary;
  const mutedColor = tokens.text.tertiary;

  const ownerLabel = ownership.ownerName?.trim() || ownership.ownerEmail?.trim() || 'another user';

  const handleDismiss = () => {
    setDismissed(true);
  };

  const dismissSx = {
    color: mutedColor,
    '&:hover': { color: titleColor, bgcolor: tokens.surface.sunken },
  } as const;

  const iconBubble = (
    <Box
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: accent,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <GroupRounded sx={{ fontSize: 14 }} />
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        bgcolor: accentSoft,
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
    >
      {/* Mobile: stacked title + body, dismiss top-right */}
      <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 1.5, py: 1.25 }}>
        <Stack spacing={0.75}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {iconBubble}
              <Typography
                fontWeight={700}
                sx={{ fontSize: '0.8125rem', lineHeight: 1.35, color: titleColor }}
              >
                Shared chat from {ownerLabel}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ ...dismissSx, p: '8px', flexShrink: 0 }}
              aria-label="Dismiss banner"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: bodyColor, lineHeight: 1.4 }}>
            You can continue this chat here. Your messages will be visible to {ownerLabel}, and
            this chat will not be saved to your workspace.
          </Typography>
        </Stack>
      </Box>

      {/* sm+: single horizontal row */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          px: { sm: 2, md: 3 },
          py: 0.75,
          alignItems: 'center',
          gap: 1.5,
          minHeight: 40,
        }}
      >
        {iconBubble}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', gap: 0.5 }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: '0.8125rem', color: titleColor, whiteSpace: 'nowrap' }}
          >
            Shared chat from {ownerLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: mutedColor, whiteSpace: 'nowrap' }}>·</Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8125rem', color: bodyColor, lineHeight: 1.35 }}
          >
            You can continue the chat here - your messages will be visible to {ownerLabel}, and
            this chat will not be saved to your workspace.
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{ ...dismissSx, flexShrink: 0 }}
          aria-label="Dismiss banner"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
